import { AxiosError, AxiosResponse } from 'axios'
import { useGeckoAuth } from 'features/auth'
import { RolodexContext } from 'features/rolodex/RolodexProvider'
import groupBy from 'lodash/groupBy'
import isNil from 'lodash/isNil'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import useSWR from 'swr'
import { RolodexFilePath, RolodexSignedURLResult } from 'types'

/**
 * A hook for getting the signed URLs required to download a given Rolodex file
 * @param queryOrQueries The query body, or an array of bodies, or undefined
 * @param headers Optional headers to pass along when requesting the url from GCP
 */
export const useRolodexSignedURLs = (
  queryOrQueries: RolodexFilePath | ReadonlyArray<RolodexFilePath> | undefined,
  headers?: Record<string, string>
): {
  /** The first url returned, or undefined */
  url: string | undefined
  /** All urls returned, or undefined */
  urls: ReadonlyArray<string>
  /** Signed URLs, paired with their Rolodex entry IDs and paths */
  results: ReadonlyArray<RolodexSignedURLResult>
  /** Whether the SWR query is still loading */
  isLoading: boolean
  /** Whether the SWR query is still validating */
  isValidating: boolean
  /** Any errors returned from SWR.
   * First error value is from main fetching.
   * Second error value is from cache fetch. */
  errors: ReadonlyArray<AxiosError>
  /** To account for the initial case before the query fires  */
  hasEverLoaded: boolean
  /** A callback for forcing a re-fetch */
  forceRefresh: () => Promise<void>
} => {
  //----------------------------------------------------------------------------

  const { fileCache } = useContext(RolodexContext)

  const { fileCacheQuery, rolodexQuery } = useMemo<{
    fileCacheQuery: ReadonlyArray<RolodexFilePath> | undefined
    rolodexQuery: ReadonlyArray<RolodexFilePath> | undefined
  }>(() => {
    if (!queryOrQueries)
      return { rolodexQuery: undefined, fileCacheQuery: undefined }
    const queryAsArr: ReadonlyArray<RolodexFilePath> = Array.isArray(
      queryOrQueries
    )
      ? queryOrQueries
      : [queryOrQueries]

    const groupedUrls = groupBy(queryAsArr, ({ path, entry_id }) => {
      const isInCache = Array.from(fileCache.entries()).find(
        ([filePathStr]) => {
          const { entry_id: cacheEntryId, path: cachePath } = JSON.parse(
            filePathStr
          ) as RolodexFilePath
          return cacheEntryId === entry_id && cachePath === path
        }
      )
      return isInCache ? 'cachedUrlsToQuery' : 'rolodexUrlsToQuery'
    })

    return {
      fileCacheQuery: groupedUrls['cachedUrlsToQuery'],
      rolodexQuery: groupedUrls['rolodexUrlsToQuery'],
    }
  }, [queryOrQueries, fileCache])

  const { fetchSignedUrlFromRolodexOnly, fetchSignedUrlFromFileCacheOnly } =
    useFileSignedUrlFetcher()

  //---------------------------------------------------------------------------
  // Rolodex Proper Fetch

  const {
    data: rolodexProperData,
    isLoading: rolodexProperIsLoading,
    isValidating: rolodexProperIsValidating,
    error: swrError,
    mutate: rolodexProperMutate,
  } = useSWR(
    isNil(rolodexQuery)
      ? null
      : [
          { queryOrQueries: rolodexQuery, headers },
          'signed_url_rolodex_proper',
        ],
    (keys) => fetchSignedUrlFromRolodexOnly(keys[0]),
    {
      revalidateIfStale: false,
      shouldRetryOnError: true,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      // Ten minutes, due to signed URL expiration
      refreshInterval: 10 * 60 * 1000,
    }
  )

  //---------------------------------------------------------------------------
  // Rolodex Cache "Fetch"
  //  This needs to be split from the Rolodex Proper Fetch because we need to
  //  call `mutate()` when the file-cache changes.  If these 2 calls were 1,
  //  this would cause Rolodex to re-fetch and return a fresh signed URL,
  //  which would re-trigger the cache calculations, which would update the file-cache,
  //  which would `mutate()` Rolodex fetch, re-starting the cycle.

  const fileCacheRef = useRef(fileCache)

  const {
    data: fileCacheData,
    isLoading: fileCacheIsLoading,
    isValidating: fileCacheIsValidating,
    error: swrCacheQueryError,
    mutate: fileCacheMutate,
  } = useSWR(
    isNil(fileCacheQuery)
      ? null
      : [{ queryOrQueries: fileCacheQuery, headers }, 'signed_url_file_cache'],
    (keys) => fetchSignedUrlFromFileCacheOnly(keys[0]),
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    }
  )
  //
  // If the fileCache has changed, re-fetch the data -- the key will not catch this
  useEffect(() => {
    if (fileCache !== fileCacheRef.current) {
      fileCacheMutate(fileCacheData).then(() => {})
    }
    fileCacheRef.current = fileCache
  }, [fileCacheData, fileCache, fileCacheMutate])

  //----------------------------------------------------------------------------
  // Merge the fetch results

  const isLoading = rolodexProperIsLoading || fileCacheIsLoading

  const isValidating = rolodexProperIsValidating || fileCacheIsValidating

  const forceRefresh = useCallback(async () => {
    await rolodexProperMutate().then(() => {})
    await fileCacheMutate().then(() => {})
  }, [fileCacheMutate, rolodexProperMutate])

  return useMemo(() => {
    // TODO CAN-152: This DOES NOT preserve any sorting order / the order of `queryOrQueries`
    //  -- Is this required? If so, we need to implement
    return {
      results: [
        ...(fileCacheData?.results ?? []),
        ...(rolodexProperData?.results ?? []),
      ],
      urls: [
        ...(fileCacheData?.urls ?? []),
        ...(rolodexProperData?.urls ?? []),
      ],
      url: fileCacheData?.urls?.at(0) ?? rolodexProperData?.urls?.at(0),
      isLoading,
      isValidating,
      errors: [swrError, swrCacheQueryError],
      hasEverLoaded: !!fileCacheData && !!rolodexProperData,
      forceRefresh,
    }
  }, [
    fileCacheData,
    rolodexProperData,
    isLoading,
    isValidating,
    swrError,
    swrCacheQueryError,
    forceRefresh,
  ])
}

// ----------------------------------------------------------------------------

/**
 * A hook that returns 3 callbacks which, when called, will return the signed
 * URLs for a given Rolodex file from either:
 * - Just Rolodex proper (`fetchSignedUrlFromRolodex`)
 * - Just the file cache (`fetchSignedUrlFromFileCache`)
 * - The file cache if it exists in the file cache, otherwise Rolodex proper (`fetchSignedUrl`)
 *
 * We need to return these three options because in some cases you will need to split
 * the signed URL fetching into two separate calls, one for the file cache and one for
 * Rolodex proper to avoid an infinite loop of re-fetching (i.e. if your using SWR, you'll
 * want to split it up so that you can call `.mutate` on the file-cache fetch without mutating the
 * Rolodex-proper fetch, or else you'll likely re-create the data in the file-cache, which would
 * then require another fetch.
 */
export function useFileSignedUrlFetcher() {
  const { authorizedAxios: axios } = useGeckoAuth()
  const { apiURL, fileCache } = useContext(RolodexContext)

  // TODO ENT-1027: There should be a way to force-refresh the signed URL if,
  //  say, a CDF's generation updates, indicating a new file was uploaded
  const fetchSignedUrlFromRolodexOnly = useCallback(
    async (props: {
      queryOrQueries:
        | RolodexFilePath
        | ReadonlyArray<RolodexFilePath>
        | undefined
      headers?: Record<string, string>
    }): Promise<
      | {
          /** The first url returned, or undefined */
          url: string | undefined
          /** All urls returned, or undefined */
          urls: ReadonlyArray<string>
          /** Signed URLs, paired with their Rolodex entry IDs and paths */
          results: ReadonlyArray<RolodexSignedURLResult>
        }
      | undefined
    > => {
      const { queryOrQueries, headers } = props

      const fetchParams = translateQueryToFetchParams(
        apiURL,
        queryOrQueries,
        headers
      )

      if (!fetchParams) return undefined

      const results = await Promise.all(
        fetchParams.map<Promise<RolodexSignedURLResult>>(async (query) => {
          const splitURL = query.url.split('/')
          const filesIndex = splitURL.findIndex((s) => s === 'files')
          const entry_id = splitURL.at(filesIndex + 1) ?? ''
          const path = splitURL.slice(filesIndex + 2).join('/') ?? ''
          const signed_url = await axios
            .get(query.url, { headers: query.headers })
            .then(async (res: AxiosResponse) => {
              return await res.data
            })
            .catch((error: AxiosError) => {
              const errorMessage = `Issue grabbing rolodex storage signed URL for entry with id=[${entry_id}] and path ${path}`
              console.error(errorMessage, error)
              error.message = errorMessage
              throw error // propagate error so clients can process it in `{error} = useSWR()`
            })
          return { entry_id, path, signed_url }
        })
      )

      return {
        results,
        urls: results.map(({ signed_url }) => signed_url),
        url: results.at(0)?.signed_url,
      }
    },
    [apiURL, axios]
  )

  const fetchSignedUrlFromFileCacheOnly = useCallback(
    async (props: {
      queryOrQueries:
        | RolodexFilePath
        | ReadonlyArray<RolodexFilePath>
        | undefined
      headers?: Record<string, string>
    }): Promise<
      | {
          /** The first url returned, or undefined */
          url: string | undefined
          /** All urls returned, or undefined */
          urls: ReadonlyArray<string>
          /** Signed URLs, paired with their Rolodex entry IDs and paths */
          results: ReadonlyArray<RolodexSignedURLResult>
        }
      | undefined
    > => {
      const { queryOrQueries, headers } = props
      const fetchParams = translateQueryToFetchParams(
        apiURL,
        queryOrQueries,
        headers
      )

      if (!fetchParams) return undefined

      const results = await Promise.all(
        fetchParams.map<Promise<RolodexSignedURLResult>>(
          async (query): Promise<RolodexSignedURLResult> => {
            const splitURL = query.url.split('/')
            const filesIndex = splitURL.findIndex((s) => s === 'files')
            const entry_id = splitURL.at(filesIndex + 1) ?? ''
            const path = splitURL.slice(filesIndex + 2).join('/') ?? ''
            try {
              const thisEntryInCache = Array.from(fileCache.entries()).find(
                ([filePathStr]) => {
                  const { entry_id: cacheEntryId, path: cachePath } =
                    JSON.parse(filePathStr) as RolodexFilePath
                  return cacheEntryId === entry_id && cachePath === path
                }
              )

              if (isNil(thisEntryInCache)) {
                // This is not a cache-entry, this should not happen
                throw new Error(
                  `Tried accessing file-cache for an entry not in the cache -- ${entry_id} with path ${path}`
                )
              }

              // This is a cache-entry
              const [, getBlobUrlFn] = thisEntryInCache
              const signed_url = await getBlobUrlFn()
              return { entry_id, path, signed_url }
            } catch (error) {
              console.error('Issue grabbing GCP signed URL', error)
              throw error // propagate error so clients can process it in `{error} = useSWR()`
            }
          }
        )
      )

      return {
        results,
        urls: results.map(({ signed_url }) => signed_url),
        url: results.at(0)?.signed_url,
      }
    },
    [apiURL, fileCache]
  )

  const fetchSignedUrl = useCallback(
    async (props: {
      queryOrQueries:
        | RolodexFilePath
        | ReadonlyArray<RolodexFilePath>
        | undefined
      headers?: Record<string, string>
    }): Promise<
      | {
          /** The first url returned, or undefined */
          url: string | undefined
          /** All urls returned, or undefined */
          urls: ReadonlyArray<string>
          /** Signed URLs, paired with their Rolodex entry IDs and paths */
          results: ReadonlyArray<RolodexSignedURLResult>
        }
      | undefined
    > => {
      const { queryOrQueries, headers } = props
      if (!queryOrQueries) return undefined

      const queryAsArr: ReadonlyArray<RolodexFilePath> = Array.isArray(
        queryOrQueries
      )
        ? queryOrQueries
        : [queryOrQueries]
      const groupedUrls = groupBy(queryAsArr, ({ path, entry_id }) => {
        const isInCache = Array.from(fileCache.entries()).find(
          ([filePathStr]) => {
            const { entry_id: cacheEntryId, path: cachePath } = JSON.parse(
              filePathStr
            ) as RolodexFilePath
            return cacheEntryId === entry_id && cachePath === path
          }
        )
        return isInCache ? 'cachedUrlsToQuery' : 'rolodexUrlsToQuery'
      })

      const fileCacheData = await fetchSignedUrlFromFileCacheOnly({
        queryOrQueries: groupedUrls['cachedUrlsToQuery'],
        headers,
      })
      const rolodexProperData = await fetchSignedUrlFromRolodexOnly({
        queryOrQueries: groupedUrls['rolodexUrlsToQuery'],
        headers,
      })

      return {
        results: [
          ...(fileCacheData?.results ?? []),
          ...(rolodexProperData?.results ?? []),
        ],
        urls: [
          ...(fileCacheData?.urls ?? []),
          ...(rolodexProperData?.urls ?? []),
        ],
        url: fileCacheData?.urls?.at(0) ?? rolodexProperData?.urls?.at(0),
      }
    },
    [fileCache, fetchSignedUrlFromFileCacheOnly, fetchSignedUrlFromRolodexOnly]
  )

  return {
    fetchSignedUrl,
    fetchSignedUrlFromRolodexOnly,
    fetchSignedUrlFromFileCacheOnly,
  }
}

// ----------------------------------------------------------------------------

function translateQueryToFetchParams(
  apiURL: string,
  queryOrQueries: RolodexFilePath | ReadonlyArray<RolodexFilePath> | undefined,
  headers: Record<string, string> | undefined
) {
  if (
    !queryOrQueries ||
    (Array.isArray(queryOrQueries) && !queryOrQueries.length)
  ) {
    return undefined
  }
  const queryAsArr: ReadonlyArray<RolodexFilePath> = Array.isArray(
    queryOrQueries
  )
    ? queryOrQueries
    : [queryOrQueries]
  return queryAsArr?.map(({ entry_id, path }) => {
    return {
      url: `${apiURL}/api/v2/files/${entry_id}/${path}`,
      headers,
    }
  })
}
